import { useQuery } from '@tanstack/react-query'
import { api } from '@/lib/api'
import type { DepositsHeldSummary } from '@/types'

interface RecentInvoice {
  id: number
  number: string
  customer_name: string | null
  unit_name: string | null
  total: number
  balance_due: number
  paid: boolean
  overdue: boolean
  due_at: string
}

interface RecentPayment {
  id: number
  amount: number
  payment_method: string
  paid_at: string
  invoice_number: string | null
  customer_name: string | null
}

interface ExpiringLease {
  id: number
  lease_code: string
  customer_name: string | null
  unit_name: string | null
  ended_at: string
}

// EP-162 item 11: a rental lease still active past its end date. Its rent is
// no longer billed; it stays listed until the lease is renewed or ended.
interface ExpiredLease extends ExpiringLease {
  property_name: string | null
  /** Whole days from the end date to today. */
  days_since: number
}

// The same lease, where a newer live lease (on the unit, or its renewal) exists.
interface SupersededLease extends ExpiredLease {
  newer_lease_id: number
  newer_lease_code: string
}

interface DashboardRevenue {
  /** Cash received this month, against invoices from ANY month. */
  revenue_this_month: number
  /** Invoice totals whose billing period is in the current billing cycle. */
  billed_this_month: number
  /**
   * Money paid against the invoices counted in `billed_this_month` -- same
   * population, so this is the only figure that pairs with it to form a rate.
   * Do NOT compute a collection rate from `revenue_this_month`: that counts
   * payments by arrival date, so it mostly settles LAST month's invoices and
   * divides by a denominator that is near zero early in a month. Production
   * showed 6480.9% that way (EP-117). Optional because the API may be a
   * version behind the frontend.
   */
  collected_on_billed_this_month?: number
  /** Remaining balances for the same period; optional for older API versions. */
  unpaid_this_month?: number
  overdue_this_month?: number
  rent_roll?: number
  vacancy_gap?: number
  outstanding_balance: number
  overdue_amount: number
  expenses_this_month: number
  net_profit_this_month: number
}

interface DashboardOperations {
  /** Account-wide first-use flags; counts follow the selected property. */
  maintenance_used: boolean
  open_maintenance_count: number
  parcels_used: boolean
  waiting_parcel_count: number
}

interface DashboardStats {
  total_properties: number
  total_units: number
  active_leases: number
  pending_invoices: number
  occupied_units: number
  vacant_units: number
  occupancy_rate: number
  overdue_invoices: number
  revenue: DashboardRevenue
  operations?: DashboardOperations
  recent_invoices: RecentInvoice[]
  recent_payments: RecentPayment[]
  expiring_leases: ExpiringLease[]
  // EP-162 item 11. Optional: an older API omits them.
  expired_leases?: ExpiredLease[]
  superseded_leases?: SupersededLease[]
  // EP-149 T149-080 (MON-7 D): account-wide, unaffected by the property_id
  // param above. Optional: the API may be a version behind the frontend.
  deposits_held?: DepositsHeldSummary
  // EP-155: slips waiting for review (verification_status pending, every
  // period) and the meters with no reading this month in the account's
  // timezone (null on a condominium account). Optional: an older API omits them.
  payments_pending_review?: number
  meters_to_read?: MetersToRead | null
  // T156-018/019 (EP-156 §5 decision 15): expenses waiting for owner/admin
  // confirmation, property-scoped like the other to-do counts. Optional: an
  // older API omits it.
  expenses_pending_confirmation?: number
  // EP-161 §2.4: kiosk sessions awaiting payment or rejected, exited more
  // than an hour ago and not marked as fraud; property_id is the property of
  // the oldest one (null when none). Optional: an older API omits it.
  parking_unpaid?: { count: number; property_id: number | null }
}

interface MeterToRead {
  meter_id: number
  meter_type: string
  unit_id: number
  unit_number: string
  property_id: number
  property_name: string
}

interface MetersToRead {
  count: number
  /** Up to five, by building, unit and meter type. */
  items: MeterToRead[]
}

export type { DashboardStats, RecentInvoice, RecentPayment, ExpiringLease, ExpiredLease, SupersededLease, DashboardRevenue, DashboardOperations, MeterToRead, MetersToRead }

// selectedPropertyId scopes KPI tiles to the header property switcher;
// null/undefined ⇒ account-wide "All properties". It is part of the query key
// so switching properties refetches instead of serving the cached scope.
export function useDashboard(selectedPropertyId?: number | null) {
  return useQuery({
    queryKey: ['dashboard', selectedPropertyId ?? null],
    queryFn: async () => {
      const params = selectedPropertyId ? { property_id: selectedPropertyId } : undefined
      const { data } = await api.get<{ data: DashboardStats }>('/api/dashboard', { params })
      return data.data
    },
  })
}
